<script setup lang="ts">
    import { reactive, onMounted, computed } from 'vue';

    export type Emit = {
        (ev: 'countdownFinished'): void;
    };

    const emit = defineEmits<Emit>();

    const props = defineProps<{
        countdownTo: string;
        hideTimer?: boolean;
    }>();

    const state = reactive<{
        showTimer: boolean;
        days: number | null;
        hours: number | null;
        minutes: number | null;
        seconds: number | null;
    }>({
        showTimer: false,
        days: null,
        hours: null,
        minutes: null,
        seconds: null,
    });

    onMounted(async () => {
        startTimer(props.countdownTo);
    });

    const startTimer = (date: string) => {
        // Set the date we're counting down to
        const countDownDate = new Date(date).getTime();

        // Update the count down every 1 second
        const interval = setInterval(() => {
            update(countDownDate, interval);
        }, 1000);

        update(countDownDate, interval);
    };

    const update = (countDownDate: number, interval: ReturnType<typeof setInterval>) => {
        // Get current timestamp in UTC
        const now = new Date().getTime();

        // Find the distance between now an the count down date
        const timeDifferenceMs = countDownDate - now;

        // Time calculations for days, hours, minutes and seconds
        const days = Math.floor(timeDifferenceMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDifferenceMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeDifferenceMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDifferenceMs % (1000 * 60)) / 1000);

        // Display the result
        if (days >= 0) state.days = days;
        if (hours >= 0) state.hours = hours;
        if (minutes >= 0) state.minutes = minutes;
        if (seconds >= 0) state.seconds = seconds;

        // If the count down is finished
        if (timeDifferenceMs < 0) {
            clearInterval(interval);
            emit('countdownFinished');
        }

        state.showTimer = timeDifferenceMs >= 0;
    };

    const showTimer = computed(() => {
        return state.showTimer && !props.hideTimer;
    });
</script>

<template>
    <div class="text-center" v-if="showTimer">
        <div class="countdown-clock">
            <div>
                <span class="count">{{ state.days }}</span>
                <small>{{ $Resources.SendingCountdownDays }}</small>
            </div>
            <div>
                <span class="count">{{ state.hours }}</span>
                <small>{{ $Resources.SendingCountdownHours }}</small>
            </div>
            <div>
                <span class="count">{{ state.minutes }}</span>
                <small>{{ $Resources.SendingCountdownMinutes }}</small>
            </div>
            <div>
                <span class="count">{{ state.seconds }}</span>
                <small>{{ $Resources.SendingCountdownSeconds }}</small>
            </div>
        </div>
    </div>
</template>
